import styled from "@emotion/styled";
import {
  borderRadius,
  duration,
  fontWeight,
  mediaBelow,
  fontFamily,
  lineHeight,
  surface,
  text,
} from "@atlas-int/tokens";
import { fontSizes } from "../typography";
import { SectionHeader, SectionSubheader } from "../SharedUI";

const ConclusionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const ConclusionContent = styled.div`
  width: 50%;
  margin: auto;
  padding: 5em 0 2em 0;
  box-sizing: border-box;
  font-size: ${fontSizes.bodyText};
  color: ${text.primary};
  line-height: ${lineHeight.loose};

  ${mediaBelow("md")} {
    width: 90%;
    padding: 2rem 3rem 4rem 3rem;
    font-size: ${fontSizes.centeredBodyText};
  }

  @media (max-width: 530px) {
    width: 100%;
    padding: 2rem 2rem 4rem 2rem;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin: 2rem auto;
  box-sizing: border-box;
  display: flex;
  gap: 1rem;

  @media (max-width: 836px) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const StyledButton = styled.a`
  flex: 1;
  max-width: calc(33% - 1rem);
  padding: 1rem 1.5rem;
  line-height: 1.2;
  background-color: rgb(46, 151, 191);
  color: white;
  text-decoration: none;
  border-radius: ${borderRadius.md};
  font-weight: ${fontWeight.semibold};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: ${fontSizes.buttonLabels};
  transition: background-color ${duration.base} ease;

  &:hover {
    background-color: rgb(35, 130, 165);
    color: white;
  }

  @media (max-width: 836px) {
    min-width: calc(33% - 4rem);
  }
`;

const CitationText = styled.p`
  font-family: ${fontFamily.mono};
  font-size: ${fontSizes.citation};
  background-color: ${surface.panel};
  padding: 1rem;
  border-radius: ${borderRadius.base};
  margin-top: 0.5rem;
`;

const Attribution = styled.div`
  font-size: ${fontSizes.attribution};
  width: 100%;
  max-width: 100%;
  background-color: ${surface.panel};
  border-top: 1px solid rgb(216, 216, 216);
  color: ${text.secondary};
  padding: 1.5rem 2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
`;

const AttributionLine = styled.div`
  padding: 0;
  margin: 0;
  width: 50%;

  ${mediaBelow("md")} {
    width: 100%;
    padding: 0 1rem;
  }
`;

export default function Conclusion() {
  return (
    <ConclusionContainer>
      <ConclusionContent>
        <SectionHeader>
          3. The Impact of An Improved Trade Dataset
        </SectionHeader>
        <p>
          By addressing these two structural limitations of global trade data,
          the Growth Lab is able to apply these methods directly in its own
          tools and research.
        </p>
        <p>
          Trade data is first harmonized across vintages using our economically
          balanced weights. Validating against the International Monetary Fund's
          Balance of Payments data confirms that this approach accurately
          reconstructs trade patterns, even for non-reporting countries.
        </p>
        <p>
          Together, these methods recovered $861 billion in trade in 2024 alone
          and recovered 8 percent of product codes.
        </p>

        <p>
          Applied across all country pairs and six decades of trade data, this
          methodology underpins the{" "}
          <a href="https://atlas.hks.harvard.edu/">
            Atlas of Economic Complexity
          </a>
          , the Growth Lab's flagship data tool. Since 2013, the Atlas has
          supported decision-making by trade ministries, central banks,
          multinational firms, development agencies and academic researchers.
          The Atlas of Economic Complexity translates this data into accessible
          visualizations, while the underlying datasets enable rigorous
          empirical analysis.
        </p>

        <SectionHeader style={{ marginBottom: 0 }}>
          Explore The Data
        </SectionHeader>
        <p style={{ marginTop: "0.5rem" }}>
          All components of the Growth Lab's trade data methodology are openly
          available. You can reproduce the full workflow or adapt individual
          steps to build your own bilateral trade datasets for any
          classification vintage and time period.
        </p>

        <ButtonContainer>
          <StyledButton href="https://www.nature.com/articles/s41597-025-06488-2">
            Read the Research Paper
          </StyledButton>
          <StyledButton href="https://dataverse.harvard.edu/dataset.xhtml?persistentId=doi:10.7910/DVN/5NGVOB">
            Explore Mirrored Trade Data
          </StyledButton>
          <StyledButton href="https://dataverse.harvard.edu/dataset.xhtml?persistentId=doi:10.7910/DVN/6AADMR">
            Download Conversion Weights
          </StyledButton>
        </ButtonContainer>

        <SectionSubheader>Code & Tools</SectionSubheader>
        <ButtonContainer style={{ marginTop: "0.5rem" }}>
          <StyledButton href="https://github.com/harvard-growth-lab/comtrade-downloader">
            Comtrade Downloader
          </StyledButton>
          <StyledButton href="https://github.com/harvard-growth-lab/comtrade-conversion-weights">
            Conversion Weights Generator
          </StyledButton>
          <StyledButton href="https://github.com/harvard-growth-lab/comtrade-mirroring">
            Mirroring Pipeline
          </StyledButton>
        </ButtonContainer>

        <SectionSubheader style={{ marginBottom: 0 }}>Support</SectionSubheader>
        <p style={{ marginTop: "0.5rem" }}>
          For methodological questions, data access, or research use cases,
          contact{" "}
          <a href="mailto:growthlabtools@hks.harvard.edu">
            growthlabtools@hks.harvard.edu
          </a>
          .
        </p>

        <SectionHeader style={{ marginBottom: 0 }}>
          Recommended Citation
        </SectionHeader>
        <p style={{ marginTop: "0.5rem" }}>
          Please cite the paper, datasets, and code when using these resources.
        </p>
        <p className="bold" style={{ marginBottom: "0" }}>
          Paper:
        </p>
        <CitationText>
          Bustos, S., Jackson, E., Torun, D., Leonard, B., Tuzcu, N., Lukaszuk,
          P., White, A., Hausmann, R., Yildirim, M.A. (2025). "Tackling
          Discrepancies in Trade Data: The Harvard Growth Lab International
          Trade Datasets."{" "}
          <span
            className="italic"
            style={{ fontFamily: "inherit", fontSize: "inherit" }}
          >
            Scientific Data
          </span>{" "}
          <span className="bold">13</span>, 170 (2026).{" "}
          <a
            href="https://doi.org/10.1038/s41597-025-06488-2"
            style={{ overflowWrap: "break-word" }}
          >
            https://doi.org/10.1038/s41597-025-06488-2
          </a>
        </CitationText>
        <p className="bold" style={{ marginBottom: "0", marginTop: "1.5rem" }}>
          Datasets:
        </p>
        <CitationText
          style={{ overflowWrap: "break-word", wordBreak: "break-word" }}
        >
          Harvard Growth Lab. "Bilateral Trade Data Aggregated by Year," 2025.{" "}
          <a
            href="https://doi.org/10.7910/DVN/5NGVOB"
            style={{ overflowWrap: "break-word" }}
          >
            https://doi.org/10.7910/DVN/5NGVOB
          </a>
          .
        </CitationText>
        <CitationText
          style={{
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          Harvard Growth Lab. "Weighted Classification Conversion Tables," 2025.{" "}
          <a
            href="https://doi.org/10.7910/DVN/6AADMR"
            style={{ overflowWrap: "break-word" }}
          >
            https://doi.org/10.7910/DVN/6AADMR
          </a>
          .
        </CitationText>
        <p className="bold" style={{ marginBottom: "0", marginTop: "1.5rem" }}>
          Code:
        </p>
        <CitationText
          style={{
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          Harvard Growth Lab.{" "}
          <span
            className="italic"
            style={{ fontFamily: "inherit", fontSize: "inherit" }}
          >
            Comtrade Downloader
          </span>{" "}
          (2025).{" "}
          <a
            href="https://github.com/harvard-growth-lab/comtrade-downloader"
            style={{ overflowWrap: "break-word" }}
          >
            https://github.com/harvard-growth-lab/comtrade-downloader
          </a>
          .
        </CitationText>
        <CitationText
          style={{
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          Harvard Growth Lab.{" "}
          <span
            className="italic"
            style={{ fontFamily: "inherit", fontSize: "inherit" }}
          >
            Comtrade Conversion Weights Generator
          </span>{" "}
          (2025).{" "}
          <a
            href="https://github.com/harvard-growth-lab/comtrade-conversion-weights"
            style={{ overflowWrap: "break-word" }}
          >
            https://github.com/harvard-growth-lab/comtrade-conversion-weights
          </a>
          .
        </CitationText>
        <CitationText
          style={{
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          Harvard Growth Lab.{" "}
          <span
            className="italic"
            style={{ fontFamily: "inherit", fontSize: "inherit" }}
          >
            Comtrade Mirroring Pipeline
          </span>{" "}
          (2025).{" "}
          <a
            href="https://github.com/harvard-growth-lab/comtrade-mirroring"
            style={{ overflowWrap: "break-word" }}
          >
            https://github.com/harvard-growth-lab/comtrade-mirroring
          </a>
          .
        </CitationText>
      </ConclusionContent>
      <Attribution>
        <AttributionLine>
          <span className="underline">Growth Lab's Research Team:</span>{" "}
          <span className="italic">
            Sebastián Bustos, David Torun, Piotr Lukaszuk, Ricardo Hausmann &
            Muhammed A. Yıldırım
          </span>
        </AttributionLine>
        <AttributionLine>
          <span className="underline">
            Growth Lab's Digital Development & Design Team:
          </span>{" "}
          <span className="italic">
            Ellie Jackson, Tammy Zhang, Nil Tuzcu, Brendan Leonard, Robert
            Christie, Annie White
          </span>
        </AttributionLine>
      </Attribution>
    </ConclusionContainer>
  );
}
